import React, { useCallback, useEffect, useState } from 'react';
import config from '../config';

// «الأمان وسجل الدخول» — للمدير: مين دخل وإمتى ومنين، المحاولات الغلط، وإجراءات الأمان لكل مستخدم.

const API = `${config.API_BASE_URL}/api/security`;
const fmt = (d) => (d ? new Date(d).toLocaleString('ar-EG', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const device = (ua) => {
  const s = String(ua || '');
  if (!s) return '—';
  const os = /Android/i.test(s) ? 'أندرويد' : /iPhone|iPad/i.test(s) ? 'آيفون' : /Windows/i.test(s) ? 'ويندوز' : /Mac OS/i.test(s) ? 'ماك' : /Linux/i.test(s) ? 'لينكس' : '';
  const br = /Edg\//.test(s) ? 'Edge' : /Chrome\//.test(s) ? 'Chrome' : /Firefox\//.test(s) ? 'Firefox' : /Safari\//.test(s) ? 'Safari' : '';
  return [br, os].filter(Boolean).join(' · ') || s.slice(0, 30);
};
const BAD = new Set(['login_fail', 'otp_fail', 'locked']);
const tone = (ev) => (BAD.has(ev) ? 'bg-red-100 text-red-700' : ev === 'login_ok' || ev === 'sso' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700');

export default function SecurityCenter() {
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [labels, setLabels] = useState({});
  const [filter, setFilter] = useState({ username: '', failedOnly: false });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: '300' });
      if (filter.username.trim()) q.set('username', filter.username.trim());
      if (filter.failedOnly) q.set('failedOnly', '1');
      const [u, l] = await Promise.all([
        fetch(`${API}/users`).then(r => r.json()),
        fetch(`${API}/login-log?${q}`).then(r => r.json())
      ]);
      setUsers(u.users || []);
      setRows(l.rows || []);
      setLabels(l.events || {});
    } catch {
      setMsg({ type: 'error', text: 'تعذر تحميل بيانات الأمان' });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const act = async (user, action, question) => {
    if (!window.confirm(question)) return;
    try {
      const r = await fetch(`${API}/users/${user.UserId}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const d = await r.json().catch(() => ({}));
      setMsg({ type: r.ok ? 'success' : 'error', text: d.message || (r.ok ? 'تم' : 'حصلت مشكلة') });
      load();
    } catch {
      setMsg({ type: 'error', text: 'حصلت مشكلة في الاتصال' });
    }
  };

  const failedToday = rows.filter(r => BAD.has(r.Event) && Date.now() - new Date(r.At).getTime() < 86400e3).length;

  return (
    <div className="p-4 md:p-6 space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">الأمان وسجل الدخول</h1>
        <p className="text-sm text-slate-500 mt-1">
          مين دخل السيستم وإمتى ومن أنهي جهاز، والمحاولات الغلط. بعد 5 محاولات غلط الدخول بيتقفل تلقائي، والمدة بتزيد لو اتكررت.
        </p>
      </div>

      {msg && (
        <div className={`rounded-lg px-4 py-3 text-sm ${msg.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {msg.text}
          <button className="float-left text-xs opacity-60" onClick={() => setMsg(null)}>إغلاق</button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs text-slate-500">المستخدمين النشطين</div><div className="text-2xl font-bold text-slate-800">{users.filter(u => u.IsActive).length}</div></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs text-slate-500">مديرين مربوطين بالتأكيد بخطوتين</div><div className="text-2xl font-bold text-slate-800">{users.filter(u => u.IsAdmin && u.TotpEnabled).length} / {users.filter(u => u.IsAdmin && u.IsActive).length}</div></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs text-slate-500">محاولات غلط آخر 24 ساعة</div><div className={`text-2xl font-bold ${failedToday ? 'text-red-600' : 'text-slate-800'}`}>{failedToday}</div></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs text-slate-500">الموقوفين</div><div className="text-2xl font-bold text-slate-800">{users.filter(u => !u.IsActive).length}</div></div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 font-bold text-slate-800">المستخدمين</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="p-3 text-right">المستخدم</th>
                <th className="p-3 text-right">الحالة</th>
                <th className="p-3 text-right">التأكيد بخطوتين</th>
                <th className="p-3 text-right">آخر دخول</th>
                <th className="p-3 text-right">غلط النهارده</th>
                <th className="p-3 text-right">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.UserId} className="border-t border-slate-100">
                  <td className="p-3">
                    <div className="font-medium text-slate-800">{u.FullName || u.Username}</div>
                    <div className="text-xs text-slate-500">{u.Username}{u.IsAdmin ? ' · مدير' : ''}</div>
                  </td>
                  <td className="p-3">{u.IsActive ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">نشط</span> : <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-600 text-xs">موقوف</span>}</td>
                  <td className="p-3 text-xs">{u.IsAdmin ? (u.TotpEnabled ? <span className="text-green-700">مربوط</span> : <span className="text-amber-700">هيربط أول ما يدخل</span>) : <span className="text-slate-400">مش مطلوب</span>}</td>
                  <td className="p-3 text-xs text-slate-600">{fmt(u.LastLoginAt)}{u.LastLoginIP ? <div className="text-slate-400" dir="ltr">{u.LastLoginIP}</div> : null}</td>
                  <td className="p-3">{Number(u.FailsToday) ? <span className="text-red-600 font-bold">{u.FailsToday}</span> : <span className="text-slate-400">0</span>}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-2">
                      <button className="px-2 py-1 rounded border border-slate-300 text-xs hover:bg-slate-50" onClick={() => act(u, 'revoke-sessions', `خروج ${u.Username} من كل الأجهزة دلوقتي؟`)}>خروج من كل الأجهزة</button>
                      {Number(u.FailsToday) > 0 && <button className="px-2 py-1 rounded border border-amber-300 text-amber-800 text-xs hover:bg-amber-50" onClick={() => act(u, 'unlock', `فك قفل المحاولات الغلط لـ ${u.Username}؟`)}>فك القفل</button>}
                      {u.IsAdmin && u.TotpEnabled && <button className="px-2 py-1 rounded border border-red-300 text-red-700 text-xs hover:bg-red-50" onClick={() => act(u, 'reset-2fa', `إلغاء ربط موبايل ${u.Username}؟ (لو الموبايل ضاع أو اتغير) — هيربط موبايل جديد أول ما يدخل`)}>إعادة ربط الموبايل</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap gap-3 items-center justify-between">
          <div className="font-bold text-slate-800">سجل الدخول</div>
          <div className="flex flex-wrap gap-2 items-center">
            <input className="border border-slate-300 rounded px-2 py-1 text-sm" placeholder="اسم المستخدم" value={filter.username} onChange={e => setFilter({ ...filter, username: e.target.value })} />
            <label className="text-sm text-slate-600 flex items-center gap-1"><input type="checkbox" checked={filter.failedOnly} onChange={e => setFilter({ ...filter, failedOnly: e.target.checked })} /> الغلط بس</label>
            <button className="px-3 py-1 rounded bg-slate-800 text-white text-sm" onClick={load} disabled={loading}>{loading ? 'جاري التحميل...' : 'تحديث'}</button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[60vh]">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 sticky top-0">
              <tr>
                <th className="p-3 text-right">الوقت</th>
                <th className="p-3 text-right">المستخدم</th>
                <th className="p-3 text-right">الحدث</th>
                <th className="p-3 text-right">الجهاز</th>
                <th className="p-3 text-right">IP</th>
                <th className="p-3 text-right">تفاصيل</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.ID} className="border-t border-slate-100">
                  <td className="p-3 text-xs text-slate-600 whitespace-nowrap">{fmt(r.At)}</td>
                  <td className="p-3">{r.Username || '—'}</td>
                  <td className="p-3"><span className={`px-2 py-0.5 rounded text-xs ${tone(r.Event)}`}>{labels[r.Event] || r.Event}</span></td>
                  <td className="p-3 text-xs text-slate-600">{device(r.UserAgent)}</td>
                  <td className="p-3 text-xs text-slate-500" dir="ltr">{r.IP}</td>
                  <td className="p-3 text-xs text-slate-500">{r.Detail}</td>
                </tr>
              ))}
              {!rows.length && <tr><td className="p-6 text-center text-slate-400" colSpan={6}>مفيش أحداث</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
